import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import cardSpotImg from '../../../assets/image/lendTech/cardSpotImg.jpg';
import Button from '../../../components/button';
import MainFooter from '../../../components/mazraeKit/footers/footerMain';
import ChatHeaderIcon from '../../../components/mazraeKit/headers/ChatHeaderIcon/ChatHeaderIcon';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import NotificationHeaderIcon from '../../../components/mazraeKit/headers/NotificationHeaderIcon/NotificationHeaderIcon';
import PageLoader from '../../../components/pageLoader';
import {getPersianDateTimeV2} from '../../../utilities/helper/GetPersianDateTime';
import NotFound from '../../notFound';
import useGetBankAccountInfo from './api/getBankAccountInfo/useGetBankAccountInfo';
import useGetCashBackCardDetail from './api/getCashBackCardDetail/useGetCashBackCardDetail';
import {CashBackStatus} from './api/getCashBackCards/cashBackCards.types';
import usePatchBankAccountInfo from './api/patchBankAccountInfo/usePostBankAccountInfo';
import {IPostBankAccountInfoPayload} from './api/postBankAccountInfo/postBankAccountInfo';
import usePostBankAccountInfo from './api/postBankAccountInfo/usePostBankAccountInfo';
import usePostWithdrawRequest from './api/postWithdrawRequest/usePostWithdrawRequest';
import BankAccountDataFill from './components/BankAccountDataFill/BankAccountDataFill';
import BankAccountDataFillBottomSheet from './components/BankAccountDataFill/BankAccountDataFillBottomSheet';
import CashBackInfoSheet, {CashbackSheet} from './components/CashBackInfoSheet';
import CashOutModal from './components/CashOutModal';
import StatsGrid from './components/StatsGrid';
import {calculateCashBackRestCredits} from './helpers/cashBackAmountAmountHelpers';
import generateInfoListData from './helpers/generateInfoListData';
import getCashBackHeaderText from './helpers/getCashBackHeaderText';

const CashBackDetailsPage = () => {
  const navigate = useNavigate();
  const {cashBackGuid} = useParams();

  const {data: cashBackDetails, isLoading: isCashBackDetailsLoading} =
    useGetCashBackCardDetail(cashBackGuid);
  const {data: bankAccountInfo, isLoading: isBankInfoLoading} =
    useGetBankAccountInfo();
  const {
    mutate: createBankAccountInfo,
    isLoading: isCreateBankAccountInfoLoading,
  } = usePostBankAccountInfo();

  const {mutate: editBankAccountInfo, isLoading: isEditBankAccountInfoLoading} =
    usePatchBankAccountInfo();

  const {
    mutate: CreateWithdrawRequest,
    isLoading: isCreateWithdrawRequestLoading,
  } = usePostWithdrawRequest();

  const [activeSheet, setActiveSheet] = useState<CashbackSheet>(null);
  const [openCashOutModal, setOpenCashOutModal] = useState(false);
  const [openAccountDataFillBottomSheet, setOpenAccountDataFillBottomSheet] =
    useState(false);

  if (isCashBackDetailsLoading) {
    return <PageLoader />;
  }

  if (!isCashBackDetailsLoading && !cashBackDetails?.guid) {
    return <NotFound />;
  }

  const cashBackHeaderText = getCashBackHeaderText(cashBackDetails?.status);

  const stats = generateInfoListData(cashBackDetails, bankAccountInfo, () =>
    setActiveSheet('active-credit'),
  );

  const isWithdrawable =
    Boolean(cashBackDetails?.withdrawal_available_at) &&
    new Date(cashBackDetails?.withdrawal_available_at) <= new Date() &&
    [CashBackStatus.Active, CashBackStatus.Expired].includes(
      cashBackDetails?.status,
    );

  const AccountDataClickHandler = ({
    bank_account_number,
    iban_number,
    bank_title,
  }: IPostBankAccountInfoPayload) => {
    if (bankAccountInfo?.guid) {
      editBankAccountInfo(
        {
          guid: bankAccountInfo?.guid,
          bank_account_number,
          iban_number,
          bank_title: bank_title ?? 'اسم بانک',
        },
        {onSuccess: () => setOpenAccountDataFillBottomSheet(false)},
      );
      return;
    }
    createBankAccountInfo(
      {
        bank_account_number,
        iban_number,
        bank_title: bank_title ?? 'اسم بانک',
      },
      {onSuccess: () => setOpenAccountDataFillBottomSheet(false)},
    );
  };

  const handleCreateWithdraw = () => {
    if (!bankAccountInfo || !cashBackGuid) return;

    CreateWithdrawRequest(cashBackGuid, {
      onSuccess: () => window.location.reload(),
    });
  };

  const handleOpenCreateWithdraw = () => {
    if (!Boolean(bankAccountInfo?.guid)) {
      toast.error('برای برداشت وجه نیاز به افزودن اطلاعات حساب است.');
      return;
    }

    setOpenCashOutModal(true);
  };

  const cashbackRestCredits =
    cashBackDetails?.status !== CashBackStatus.RequestToWithdrawal
      ? calculateCashBackRestCredits(
          cashBackDetails?.amount,
          cashBackDetails?.used_amount,
          cashBackDetails?.reserved_amount,
        ).toLocaleString() + ' تومان'
      : 0;

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      {/* Header */}
      <CustomHeader
        preComponent={() => (
          <div
            className="relative flex items-center gap-2 cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <i className="icon-nameArrow---Right text-[25px] text-gray-700" />
            <span className="font-medium">اعتبار من</span>
          </div>
        )}
        postComponent={() => (
          <div className="flex gap-3 ">
            <NotificationHeaderIcon />
            <ChatHeaderIcon />
          </div>
        )}
      />

      {/* Content */}
      <div className="flex flex-col w-full h-full gap-3 p-[1.5rem] overflow-y-auto">
        <>
          <section className="flex flex-col gap-[1.5rem]  text-sm rounded-[20px]">
            <div className="relative w-full overflow-hidden rounded-lg bg-[#F5FBEE] p-[1.5rem]">
              <img
                src={cardSpotImg}
                alt=""
                className="absolute top-0 right-0 object-contain w-auto h-full"
                style={{mixBlendMode: 'multiply'}}
              />

              <div className="flex flex-col gap-4">
                <div className="flex gap-1">
                  <span className="text-sm font-medium text-right">
                    اعتبار بازگشتی
                  </span>
                  <i
                    className="text-lg icon-namereport text-farm-shop-500"
                    onClick={() => setActiveSheet('cash-back')}
                  />
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-xs">باقی مانده اعتبار</span>
                  <span className="text-xl font-bold text-center text-gray-800">
                    {cashbackRestCredits}
                  </span>
                </div>
                <div className="flex justify-end gap-2 text-sm">
                  <span>انقضا</span>
                  <span>
                    {getPersianDateTimeV2(cashBackDetails?.expires_at)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 ">
              <span className="text-sm text-gray-600">
                {cashBackHeaderText}
              </span>

              {isWithdrawable && (
                <BankAccountDataFill
                  isLoading={isBankInfoLoading}
                  bankAccountInfo={bankAccountInfo}
                  onClick={() => setOpenAccountDataFillBottomSheet(true)}
                />
              )}
            </div>
            <StatsGrid stats={stats} />

            {cashBackDetails?.status === CashBackStatus.Active && (
              <div
                className="flex gap-1 text-farm-shop-500"
                onClick={() =>
                  navigate(`/cash-back-transaction-logs/${cashBackGuid}`)
                }
              >
                <i className="text-lg icon-nameReceipt-bill" />
                <span className="text-sm font-semibold">
                  جزئیات افزایش و کاهش اعتبار
                </span>
              </div>
            )}
            <div className="fixed left-0 right-0 z-50 bottom-0 !bg-white shadow-[0_-1px_6px_0_rgba(0,0,0,0.1)]">
              <div className="p-3">
                {isWithdrawable ? (
                  <div className="flex gap-6 ">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleOpenCreateWithdraw}
                    >
                      برداشت وجه
                    </Button>
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => navigate('/home')}
                    >
                      خرید از فروشگاه
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => navigate(-1)}
                  >
                    بستن
                  </Button>
                )}
              </div>
              <MainFooter isScrolling={false} />
            </div>
          </section>

          <CashBackInfoSheet
            type={activeSheet}
            open={activeSheet !== null}
            onClose={() => setActiveSheet(null)}
          />
          <CashOutModal
            open={openCashOutModal}
            setOpen={setOpenCashOutModal}
            onClick={handleCreateWithdraw}
            isLoading={isCreateWithdrawRequestLoading}
          />
          <BankAccountDataFillBottomSheet
            bankAccountData={bankAccountInfo}
            open={openAccountDataFillBottomSheet}
            isLoading={
              isCreateBankAccountInfoLoading || isEditBankAccountInfoLoading
            }
            onClose={() => setOpenAccountDataFillBottomSheet(false)}
            onSubmit={({bank_account_number, iban_number, bank_title}) =>
              AccountDataClickHandler({
                bank_account_number,
                iban_number,
                bank_title,
              })
            }
          />
        </>
      </div>
    </div>
  );
};

export default CashBackDetailsPage;
